import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * The Project picked for production, per preview tenant (ADR 0008 addendum).
 *
 * The tenant is a grouping attribute of a Project's preview target, never an
 * owner: a pick is a pointer the Editor keeps, and it flows into no candidate,
 * publication or promotion handoff. Reading is `project.read`; changing it
 * needs `signoff.create` (an acceptance decision), which the surface gates and
 * the service enforces.
 */
export type TenantProjectPick = components["schemas"]["TenantProjectPick"];
export type TenantPickList = components["schemas"]["TenantPickList"];
export type PutTenantPickRequest =
  components["schemas"]["PutTenantPickRequest"];

export function useTenantPicksQuery(): UseQueryResult<TenantPickList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tenantPicks,
    async queryFn() {
      return unwrap(await client.GET("/v1/tenant-picks"));
    },
  });
}

export function usePutTenantPickMutation(): UseMutationResult<
  TenantProjectPick,
  unknown,
  { previewTenantId: string } & PutTenantPickRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn({ previewTenantId, ...body }) {
      return unwrap(
        await client.PUT("/v1/tenant-picks/{previewTenantId}", {
          params: { path: { previewTenantId } },
          body,
        }),
      );
    },
    retry: false,
    // Success AND a stale refusal are both news about the picks, not about
    // the request: re-read either way. The Tenants view names the pick too.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantPicks }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tenants }),
      ]),
  });
}

/** Why a pick was refused, by kind; the sentences live with the surface. */
export type TenantPickFailure =
  /** 409 — someone changed this tenant's pick first; the list has been re-read. */
  | { kind: "stale" }
  /** 422 on `projectId` — the Project's target is another tenant, or it is archived. */
  | { kind: "project_ineligible" }
  /** 404 — the Project is not visible to this account. */
  | { kind: "project_unavailable" }
  | { kind: "denied" }
  | { kind: "error" };

export function classifyTenantPickFailure(error: unknown): TenantPickFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "error" };
  }
  if (error.isConflict) {
    return { kind: "stale" };
  }
  if (error.isForbidden || error.isUnauthenticated) {
    return { kind: "denied" };
  }
  if (error.status === 404) {
    return { kind: "project_unavailable" };
  }
  if (error.status === 422) {
    return { kind: "project_ineligible" };
  }
  return { kind: "error" };
}
